import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Download, Trash2, Lock, Eye, Bell, BarChart,
  Users, CheckCircle, AlertTriangle, Loader2, FileText, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CONSENT_LABELS = {
  essential: {
    label: 'Cookie Essenziali',
    description: 'Necessari per il funzionamento del sito. Non possono essere disattivati.',
    icon: Lock,
    required: true
  },
  marketing: {
    label: 'Comunicazioni Marketing',
    description: 'Ricevi comunicazioni promozionali e offerte speciali via email.',
    icon: Bell,
    required: false
  },
  analytics: {
    label: 'Analytics',
    description: 'Ci aiuta a migliorare il servizio analizzando come utilizzi la piattaforma.',
    icon: BarChart,
    required: false
  },
  profiling: {
    label: 'Profilazione',
    description: 'Permette di personalizzare i contenuti in base ai tuoi interessi.',
    icon: Eye,
    required: false
  },
  third_party: {
    label: 'Condivisione con Terze Parti',
    description: 'Condivisione dei dati con partner selezionati per servizi aggiuntivi.',
    icon: Users,
    required: false
  },
  newsletter: {
    label: 'Newsletter',
    description: 'Ricevi aggiornamenti settimanali sul mercato immobiliare.',
    icon: FileText,
    required: false
  }
};

export default function PrivacyDashboard({ token, user }) {
  const [consents, setConsents] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const fetchConsents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/gdpr/consents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsents(res.data.consents || {});
    } catch (err) {
      console.error('Error fetching consents:', err);
      toast.error('Errore nel caricamento delle preferenze');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  const handleConsentChange = async (type, value) => {
    // Essential is always required
    if (type === 'essential') return;
    
    const newConsents = { ...consents, [type]: value };
    setConsents(newConsents);
    
    setSaving(true);
    try {
      await axios.put(`${API}/api/gdpr/consents`, 
        { consents: { [type]: value } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preferenza aggiornata');
    } catch (err) {
      toast.error('Errore nel salvataggio');
      // Revert
      setConsents(consents);
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const res = await axios.post(`${API}/api/gdpr/export-request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setExportStatus(res.data);
      toast.success('Export completato!');
      
      // Auto-download
      if (res.data.download_url) {
        const downloadRes = await axios.get(`${API}${res.data.download_url}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const blob = new Blob([JSON.stringify(downloadRes.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `weqasa_data_export_${format(new Date(), 'yyyyMMdd')}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Errore nell\'esportazione dei dati');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteRequest = async () => {
    setDeleting(true);
    try {
      await axios.post(`${API}/api/gdpr/deletion-request`,
        { reason: deleteReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Richiesta di cancellazione inviata', {
        description: 'Sarà processata entro 30 giorni'
      });
      setShowDeleteDialog(false);
      setDeleteReason('');
    } catch (err) {
      toast.error('Errore nell\'invio della richiesta');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-8 px-4" data-testid="privacy-dashboard">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-4">
          <Shield className="h-8 w-8 text-blue-600" />
        </div>
        <h1 className="text-3xl font-semibold text-gray-900">Privacy & Dati</h1>
        <p className="text-gray-500 mt-2 max-w-lg mx-auto">
          Gestisci le tue preferenze sulla privacy e i tuoi dati personali in conformità con il GDPR.
        </p>
      </div>

      {/* Consent Management */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <Lock className="h-5 w-5 text-blue-600" />
            <span>Gestione Consensi</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Controlla come utilizziamo i tuoi dati
          </p>
        </div>
        
        <div className="divide-y divide-gray-100">
          {Object.entries(CONSENT_LABELS).map(([key, config]) => {
            const IconComponent = config.icon;
            return (
              <div key={key} className="p-6 flex items-start justify-between" data-testid={`consent-${key}`}>
                <div className="flex items-start space-x-4">
                  <div className={`p-2 rounded-lg ${config.required ? 'bg-gray-100' : 'bg-blue-50'}`}>
                    <IconComponent className={`h-5 w-5 ${config.required ? 'text-gray-500' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <Label className="text-base font-medium text-gray-900">
                      {config.label}
                      {config.required && (
                        <Badge variant="secondary" className="ml-2 text-xs">Obbligatorio</Badge>
                      )}
                    </Label>
                    <p className="text-sm text-gray-500 mt-1 max-w-md">
                      {config.description}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={consents[key] || false}
                  onCheckedChange={(value) => handleConsentChange(key, value)}
                  disabled={config.required || saving}
                  data-testid={`consent-switch-${key}`}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Data Export */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <Download className="h-5 w-5 text-green-600" />
              <span>Esporta i tuoi dati</span>
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-lg">
              Scarica una copia di tutti i tuoi dati personali in formato JSON.
              Questo include profilo, proprietà, messaggi e transazioni.
            </p>
            
            {exportStatus && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Export completato</span>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  Categorie incluse: {exportStatus.data_categories?.join(', ')}
                </p>
              </div>
            )}
          </div>
          
          <Button
            onClick={handleExportData}
            disabled={exportLoading}
            className="bg-green-600 hover:bg-green-700"
            data-testid="export-data-btn"
          >
            {exportLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Esporta Dati
          </Button>
        </div>
      </div>

      {/* Data Deletion */}
      <div className="bg-white rounded-2xl border border-red-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              <span>Elimina Account e Dati</span>
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-lg">
              Richiedi la cancellazione completa del tuo account e di tutti i dati associati.
              Questa azione è irreversibile e sarà processata entro 30 giorni.
            </p>
            
            <div className="mt-4 p-4 bg-amber-50 rounded-lg">
              <div className="flex items-start space-x-2 text-amber-700">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Attenzione</p>
                  <p>La cancellazione comporterà la perdita di accesso a tutte le proprietà, mandati e comunicazioni.</p>
                </div>
              </div>
            </div>
          </div>
          
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-account-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Richiedi Cancellazione
          </Button>
        </div>
      </div>

      {/* Info Section */}
      <div className="text-center text-sm text-gray-500 space-y-2">
        <p>
          Per qualsiasi domanda sulla privacy, contattaci a{' '}
          <a href="mailto:privacy@weqasa.it" className="text-blue-600 hover:underline">
            privacy@weqasa.it
          </a>
        </p>
        <p className="flex items-center justify-center space-x-1">
          <Clock className="h-4 w-4" />
          <span>Ultimo aggiornamento privacy policy: Gennaio 2026</span>
        </p>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              <span>Conferma Cancellazione</span>
            </DialogTitle>
            <DialogDescription>
              Stai richiedendo la cancellazione permanente del tuo account e di tutti i dati associati.
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motivo della cancellazione (opzionale)</Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Aiutaci a migliorare spiegando perché vuoi cancellare il tuo account..."
                rows={3}
              />
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg text-sm text-red-800">
              <p className="font-medium">Cosa verrà cancellato:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Il tuo profilo e informazioni personali</li>
                <li>Tutte le proprietà pubblicate</li>
                <li>I messaggi nelle chat (anonimizzati)</li>
                <li>Lo storico delle transazioni</li>
                <li>Tutte le preferenze e consensi</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRequest}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Conferma Cancellazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
